import type { ReactNode } from "react";
import React, { useState, useEffect, useCallback } from "react";
import { IntlProvider } from "react-intl";
import to from "await-to-js";

import { languageConstants } from "../../../js/constants";
import { IntlLoader } from "../../../translations";
import { usePreferredLocale } from "@certa/common/hooks/language/usePreferredLocale";

type AsyncIntlProviderProps = {
  children: ReactNode;
};

type IntlStrings = Record<string, string>;

export const AsyncIntlProvider = ({ children }: AsyncIntlProviderProps) => {
  const [intlStrings, setIntlStrings] = useState<IntlStrings | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const preferredLocale = usePreferredLocale();

  const loadLanguage = useCallback(
    async (languageOverride?: string) => {
      const language = languageOverride || preferredLocale;
      const loaderFn = IntlLoader[language];
      const [error, result] = await to(loaderFn());

      if (error) {
        if (language !== languageConstants.DEFAULT_LOCALE) {
          loadLanguage(languageConstants.DEFAULT_LOCALE);
          return;
        } else {
          // Throw an error with full context so user sees the Error page (and can submit feedback, if needed)
          const detailedError = new Error(
            `Error loading language: ${language}. ` +
              `Reason: ${error?.message || "Unknown error"}. ` +
              `Type: ${error?.name || "Error"}. ` +
              `Online: ${navigator.onLine}`
          );
          // Preserve the original error stack
          detailedError.cause = error;
          throw detailedError;
        }
      }

      if (result) {
        setIntlStrings(result.default);
        setLocale(language);
      }
    },
    [preferredLocale]
  );

  useEffect(() => {
    loadLanguage(undefined);
  }, [loadLanguage]);

  if (!intlStrings || !locale) return <></>;

  return (
    <IntlProvider textComponent="span" locale={locale} messages={intlStrings}>
      <>{children}</>
    </IntlProvider>
  );
};
